import {
  IAnswer,
  IOptions,
  IQuestion,
  ISurvey,
  PatientSurvey,
  TSurvey
} from "../types/types"

export const getPatientSurvey = (
  patient_surveys: Array<PatientSurvey>,
  patientId: number | undefined
): PatientSurvey | undefined =>
  patientId
    ? patient_surveys.find(
        (patient_survey) => patient_survey.patient_id === patientId
      )
    : undefined

export const findQuestionIdByRef = (
  survey: ISurvey | undefined,
  question_reference: string
): number | undefined =>
  survey?.questions.find((_q) => _q.question_reference === question_reference)
    ?.id

export const findQuestionRefById = (
  survey: ISurvey | undefined,
  question_id: number
): string | undefined =>
  survey?.questions.find((_q) => _q.id === question_id)?.question_reference

export const findAnswerbyQuestionId = (
  patientSurvey: PatientSurvey | undefined,
  questionId: number
): string | number | Array<number> | undefined =>
  patientSurvey?.submission?.answers.find(
    (_ans: IAnswer) => _ans.question_id === questionId
  )?.value

export const pickPatientSurvey = (
  patientsSurveys: Array<PatientSurvey> | undefined,
  patientSurveyRef: string
) =>
  patientsSurveys?.find(
    (_ps: PatientSurvey) => _ps.survey_reference === patientSurveyRef
  )

export const determinePatientSurveyCompletion = (
  patientsSurvey: PatientSurvey | undefined,
  questionIds: Array<number> | undefined
) => {
  const completion = patientsSurvey?.submission?.answers.reduce((acc, _ans) => {
    return acc + ((questionIds?.indexOf(_ans.question_id) ?? -1) > -1 ? 1 : 0)
  }, 0)
  const completionDecimal = (completion ?? 0) / (questionIds?.length ?? 0)
  return completionDecimal * 100
}

export const getQuestionIdsInSurvey = (survey: ISurvey | undefined) =>
  survey?.questions.map((question) => question.id)

export const getSurveyPart = (
  survey: ISurvey,
  questionReferences: Array<string>
) => {
  const _questions =
    survey.questions.filter((_q) =>
      questionReferences.includes(_q.question_reference)
    ) ?? ([] as IQuestion[])
  const sorted_questions = sortByQuestionList(questionReferences, _questions)

  return {
    ...survey,
    questions: sorted_questions
  }
}

export const checkQuestionsAnswered = (
  survey: ISurvey,
  answers: Array<IAnswer>
) =>
  survey?.questions.every((question) => {
    const answer = answers?.find(
      (answer) => answer.question_id === question?.id
    )
    return answer !== undefined
  })

export const findSubmissionValueToQuestion = (
  question: IQuestion,
  patientSurvey: PatientSurvey
) => {
  const ans = patientSurvey.submission.answers.find(
    (_ans) => _ans.question_id === question.id
  )
  if (ans && question) {
    if (question.kind !== "NUMBER") {
      return question.options?.find((_opt) => _opt.value === ans?.value)?.label
    } else return ans.value
  }
}

export const sortByList = (list: string[], arr: ISurvey[]) => {
  const tmpMap = arr.reduce(
    (acc: { [key: string]: ISurvey }, item: ISurvey) => {
      acc[item.survey_reference] = item
      return acc
    },
    {}
  )

  return list.map((key) => tmpMap[key])
}

export const sortByQuestionList = (list: string[], arr: IQuestion[]) => {
  const tmpMap = arr.reduce(
    (acc: { [key: string]: IQuestion }, item: IQuestion) => {
      acc[item.question_reference] = item
      return acc
    },
    {}
  )

  return list.map((key) => tmpMap[key])
}

export const checkValueIsPresentInAnswer = (
  questionId: number,
  answers: Array<IAnswer>,
  value: number
) => {
  const _ans = answers?.find((answer) => answer.question_id === questionId)
  if (_ans && Array.isArray(_ans.value)) {
    const idx = _ans.value.indexOf(value)
    return idx > -1
  }
  return false
}

export const isAnswerChecked = (
  questionId: number,
  answers: Array<IAnswer>,
  value: number
) => {
  const _ans = answers?.find((answer) => answer.question_id === questionId)
  return _ans ? value === _ans.value : false
}

export const findAnswerValue = (
  questionId: number,
  answers: Array<IAnswer>
): string | number | undefined => {
  const _ans = answers?.find((answer) => answer.question_id === questionId)
  return _ans &&
    (typeof _ans.value === "number" || typeof _ans.value === "string")
    ? _ans.value
    : undefined
}

export const findAnswerLabel = (
  qRef: string,
  val: string | number | Array<number> | undefined,
  surveysDefinition: Array<ISurvey> | undefined
) => {
  if (surveysDefinition) {
    const _question = surveysDefinition
      ?.map((_surv) =>
        _surv.questions.find((q) => q.question_reference === qRef)
      )
      .filter((_surv) => _surv !== undefined)

    return _question[0]?.options?.find((option) => option.value === val)?.label
  }
}

export const convertAnswersMultipleToArray = (answers: Array<IAnswer>) => {
  return answers.length
    ? answers.map((_ans) => {
        if (typeof _ans.value === "string" && _ans.value.includes(",")) {
          return {
            ..._ans,
            value: _ans.value.split(",").map((_val) => parseInt(_val))
          }
        }
        return _ans
      })
    : []
}

export const pickSurvey = (
  surveyReference: string,
  surveysDefinition: Array<ISurvey> | undefined
) => {
  if (surveysDefinition) {
    const _survey = surveysDefinition?.find(
      (_surv) => _surv.survey_reference === surveyReference
    )
    return _survey
  }
}

export const getAnswerValue = (
  patient_surveys: Array<PatientSurvey>,
  surveyRef: string,
  questionRef: string,
  surveysDefinition: Array<ISurvey> | undefined
) =>
  findAnswerbyQuestionId(
    pickPatientSurvey(patient_surveys, surveyRef),
    findQuestionIdByRef(
      pickSurvey(surveyRef, surveysDefinition),
      questionRef
    ) ?? 0
  )

export const getAnswer = (
  patient_surveys: Array<PatientSurvey>,
  surveyRef: string,
  questionRef: string,
  surveysDefinition: Array<ISurvey> | undefined
) => {
  const answerValue = getAnswerValue(
    patient_surveys,
    surveyRef,
    questionRef,
    surveysDefinition
  )
  return findAnswerLabel(questionRef, answerValue, surveysDefinition) ?? ""
}

export const moveElement2End = (options: Array<IOptions>, label: string) => {
  const _options = [...options]
  _options.push(
    ..._options.splice(
      _options.findIndex((v) => v.label === label),
      1
    )
  )
  return _options
}

export const getAnswerLabelFromQuestion = (
  question: IQuestion,
  answer: IAnswer
) => {
  if (question.kind === "MULTIPLE") {
    const _answer =
      typeof answer.value === "string"
        ? {
            ...answer,
            value: answer.value.split(",").map(parseInt)
          }
        : { ...answer }
    const answerLabels = question.options?.filter((option) =>
      typeof _answer.value !== "number" && typeof _answer.value !== "string"
        ? _answer.value.includes(option.value)
        : false
    )
    return answerLabels.map((_ansOpt: IOptions) => _ansOpt.label).join("/")
  }
  if (question.kind === "TEXT" || question.kind === "NUMBER")
    return answer.value.toString()
  return question.options?.find((option) => option.value === answer.value)
    ?.label
}

const orderedQuestionsForSumoSurgeryData = [
  "lumbar_diagnostics",
  "lumbar_implant_presence",
  "lumbar_implant_value",
  "lumbar_surgery_act",
  "associated_lumbar_surgery_act",
  "back_stage_operated",
  "first_stage_method",
  "second_stage_method",
  "first_stage_decompression",
  "first_stage_fusion",
  "second_stage_decompression",
  "second_stage_fusion",
  "lumbar_ghm"
]

export const orderQuestionsForSumoSurgeryDataSurvey = (
  survey: TSurvey | undefined
) =>
  survey
    ? {
        ...survey,
        questions: sortByQuestionList(
          orderedQuestionsForSumoSurgeryData,
          survey.questions
        )
      }
    : undefined
